import React, {useCallback, useEffect, useState} from 'react';
import {useAppDispatch, useAppSelector} from 'store/customer';
import {
    openCommentIndexSet,
    selectOpennCommentIndex,
} from 'store/customer/UISlice';
import {useResizeObserver} from 'shared/helpers/useResizeObserver';
import {useTextMeasurement} from 'shared/helpers/useTextMeasurement';
import {Product} from 'components/customer/Product/entity/Product';

export const LINE_HEIGHT = 13;

export const useVariationRequest = (product: Product) => {
    const dispatch = useAppDispatch();
    const openCommentIndex = useAppSelector(selectOpennCommentIndex);

    const [text, setText] = useState(product.comment);
    const [showAll, setShowAll] = useState(false);
    const [showMoreButton, setShowMoreButton] = useState(false);
    const [lines, setLines] = useState(0);

    const {getLines} = useTextMeasurement();

    const textContainer = useResizeObserver(
        (
            entries: ResizeObserverEntry[],
            container: React.MutableRefObject<HTMLDivElement>
        ) => {
            if (entries.length > 0 && !showAll) {
                const {contentRect} = entries[0];
                const {height, width} = contentRect;

                const lines = getLines(width, container, product.comment);
                const maxHeightText = lines.length * LINE_HEIGHT;

                setLines(lines.length);
                if (
                    height &&
                    height > 0 &&
                    maxHeightText >= height &&
                    lines.length > 1
                ) {
                    let text = '';
                    let fitHeight = 0;

                    lines.forEach((line) => {
                        if (fitHeight + LINE_HEIGHT < height) {
                            fitHeight += LINE_HEIGHT;
                            if (text === '') {
                                text = line;
                            } else {
                                text += ' ' + line;
                            }
                        }
                    });

                    setText(text.slice(0, -3) + '...');
                    setShowMoreButton(true);
                } else {
                    setText(product.comment);
                    setShowMoreButton(false);
                }
            }
        }
    );

    const showAllToggle = useCallback(
        (onlyShow = false) =>
            () => {
                setShowAll((showAll) => (onlyShow ? true : !showAll));
                if (!showAll) {
                    dispatch(openCommentIndexSet(Number(product.id)));
                    setText(product.comment);
                }
            },
        [showAll, product.comment, product.id]
    );

    useEffect(() => {
        if (
            typeof openCommentIndex !== 'undefined' &&
            openCommentIndex !== product.id
        ) {
            setShowAll(false);
        }
    }, [openCommentIndex]);

    return {
        lines,
        showAll,
        textContainer,
        showMoreButton,
        text,
        showAllToggle,
    };
};
